"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Button = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const react_1 = require("react");
const react_aria_1 = require("react-aria");
const components_1 = require("./");
const Css_1 = require("../Css");
const useGetRef_1 = require("../hooks/useGetRef");
const utils_1 = require("../utils");
const getInteractiveElement_1 = require("../utils/getInteractiveElement");
const useTestIds_1 = require("../utils/useTestIds");
const OverlayTrigger_1 = require("./internal/OverlayTrigger");
function Button(props) {
    const { onClick: onPress, disabled, endAdornment, menuTriggerProps, tooltip, openInNew, download, contrast = false, forceFocusStyles = false, labelInFlight, ...otherProps } = props;
    const asLink = typeof onPress === "string";
    const showExternalLinkIcon = (asLink && (0, utils_1.isAbsoluteUrl)(onPress)) || openInNew;
    const [asyncInProgress, setAsyncInProgress] = (0, react_1.useState)(false);
    const isDisabled = !!disabled;
    const ariaProps = { onPress, isDisabled: isDisabled || asyncInProgress, ...otherProps, ...menuTriggerProps };
    const { label, 
    // Default the icon based on other properties.
    icon = download ? "download" : showExternalLinkIcon ? "linkExternal" : undefined, variant = "primary", size = "sm", buttonRef, } = ariaProps;
    const ref = (0, useGetRef_1.useGetRef)(buttonRef);
    const tid = (0, useTestIds_1.useTestIds)(props, (0, OverlayTrigger_1.labelOr)(ariaProps, "button"));
    const { buttonProps, isPressed } = (0, react_aria_1.useButton)({
        ...ariaProps,
        onPress: asLink
            ? utils_1.noop
            : (e) => {
                const result = onPress(e);
                if ((0, utils_1.isPromise)(result)) {
                    setAsyncInProgress(true);
                    result.finally(() => setAsyncInProgress(false));
                }
                return result;
            },
        elementType: asLink ? "a" : "button",
    }, ref);
    const { isFocusVisible, focusProps } = (0, react_aria_1.useFocusRing)(ariaProps);
    const { hoverProps, isHovered } = (0, react_aria_1.useHover)(ariaProps);
    const { baseStyles, hoverStyles, disabledStyles, pressedStyles, focusStyles } = (0, react_1.useMemo)(() => getButtonStyles(variant, size, contrast), [variant, size, contrast]);
    const buttonContent = ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [icon && (0, jsx_runtime_1.jsx)(components_1.Icon, { xss: iconStyles[size], icon: icon }), labelInFlight && asyncInProgress ? labelInFlight : label, (endAdornment || asyncInProgress) && ((0, jsx_runtime_1.jsx)("span", { css: Css_1.Css.ml1.$, children: asyncInProgress ? (0, jsx_runtime_1.jsx)(components_1.Loader, { size: "xs", contrast: contrast }) : endAdornment }))] }));
    const buttonAttrs = {
        ref: ref,
        ...buttonProps,
        ...focusProps,
        ...hoverProps,
        className: asLink ? components_1.navLink : undefined,
        css: {
            ...Css_1.Css.buttonBase.tt("inherit").$,
            ...baseStyles,
            ...(isHovered && !isPressed ? hoverStyles : {}),
            ...(isPressed ? pressedStyles : {}),
            ...(isDisabled || asyncInProgress ? { ...disabledStyles, ...Css_1.Css.cursorNotAllowed.$ } : {}),
            ...(isFocusVisible || forceFocusStyles ? focusStyles : {}),
        },
        ...tid,
    };
    // If we're disabled b/c of a non-boolean ReactNode, or the caller specified tooltip text, then show it in a tooltip
    return (0, components_1.maybeTooltip)({
        title: (0, components_1.resolveTooltip)(disabled, tooltip),
        placement: "top",
        children: (0, getInteractiveElement_1.getButtonOrLink)(buttonContent, onPress, buttonAttrs, openInNew, download),
    });
}
exports.Button = Button;
function getButtonStyles(variant, size, contrast) {
    const styles = variantStyles(contrast)[variant];
    if (variant === "text") {
        // The text variant does not support the 'size'. The 'size' prop only effects the button's height and padding which is not relevant for this variant.
        return styles;
    }
    return {
        ...styles,
        baseStyles: { ...styles.baseStyles, ...sizeStyles[size] },
    };
}
const variantStyles = (contrast) => ({
    primary: {
        baseStyles: Css_1.Css.bgBlue600.white.$,
        hoverStyles: Css_1.Css.bgBlue700.$,
        pressedStyles: Css_1.Css.bgBlue800.$,
        disabledStyles: Css_1.Css.bgBlue200.if(contrast).gray600.bgBlue900.$,
        focusStyles: Css_1.Css.bshFocus.if(contrast).boxShadow(`0 0 0 2px ${Css_1.Palette.White}`).$,
    },
    secondary: {
        baseStyles: Css_1.Css.bgWhite.bcGray300.bw1.ba.gray800.$,
        hoverStyles: Css_1.Css.bgGray100.if(contrast).bgGray300.$,
        pressedStyles: Css_1.Css.bgGray200.if(contrast).bgGray100.$,
        disabledStyles: Css_1.Css.bgWhite.gray400.$,
        focusStyles: Css_1.Css.bshFocus.if(contrast).boxShadow(`0 0 0 2px ${Css_1.Palette.White}`).$,
    },
    tertiary: {
        baseStyles: Css_1.Css.bgTransparent.blue600.if(contrast).white.$,
        hoverStyles: Css_1.Css.bgGray100.if(contrast).bgGray700.white.$,
        pressedStyles: Css_1.Css.blue800.if(contrast).bgWhite.gray900.$,
        disabledStyles: Css_1.Css.gray400.if(contrast).gray700.$,
        focusStyles: Css_1.Css.bshFocus.if(contrast).boxShadow(`0 0 0 2px ${Css_1.Palette.Blue400}`).bgGray700.white.$,
    },
    tertiaryDanger: {
        baseStyles: Css_1.Css.bgTransparent.red600.if(contrast).red400.$,
        hoverStyles: Css_1.Css.bgGray100.if(contrast).bgGray700.white.$,
        pressedStyles: Css_1.Css.red800.if(contrast).bgWhite.gray900.$,
        disabledStyles: Css_1.Css.gray400.if(contrast).gray700.$,
        focusStyles: Css_1.Css.boxShadow(`0px 0px 0px 2px ${Css_1.Palette.White}, 0px 0px 0px 4px ${Css_1.Palette.Red500}`)
            .if(contrast)
            .boxShadow(`0px 0px 0px 2px ${Css_1.Palette.Red500}`).$,
    },
    danger: {
        baseStyles: Css_1.Css.bgRed600.white.$,
        hoverStyles: Css_1.Css.bgRed700.$,
        pressedStyles: Css_1.Css.bgRed800.$,
        disabledStyles: Css_1.Css.bgRed200.if(contrast).bgRed900.gray600.$,
        focusStyles: Css_1.Css.bshDanger.if(contrast).boxShadow(`0 0 0 2px ${Css_1.Palette.White}`).$,
    },
    text: {
        baseStyles: Css_1.Css.blue700.add("fontSize", "inherit").if(contrast).blue400.$,
        hoverStyles: Css_1.Css.blue600.if(contrast).blue300.$,
        pressedStyles: Css_1.Css.blue700.if(contrast).blue200.$,
        disabledStyles: Css_1.Css.blue300.if(contrast).blue700.$,
        focusStyles: Css_1.Css.bshFocus.if(contrast).boxShadow(`0 0 0 2px ${Css_1.Palette.White}`).$,
    },
    // Todo: handle contrast variant
    textSecondary: {
        baseStyles: Css_1.Css.gray900.add("fontSize", "inherit").$,
        hoverStyles: Css_1.Css.bgGray100.$,
        pressedStyles: Css_1.Css.gray900.$,
        disabledStyles: Css_1.Css.bgWhite.gray400.$,
        focusStyles: Css_1.Css.gray900.$,
    },
});
const sizeStyles = {
    sm: Css_1.Css.hPx(32).pxPx(12).$,
    md: Css_1.Css.hPx(40).px2.$,
    lg: Css_1.Css.hPx(48).px3.$,
};
const iconStyles = {
    sm: Css_1.Css.mrPx(4).$,
    md: Css_1.Css.mr1.$,
    lg: Css_1.Css.mrPx(10).$,
};
